// The painted collection's vocabulary and the pure helpers its card needs:
// which colours a host may pick for a template, how the event's own words are
// laid out (title fitting, the date formats each look uses), and where a
// template's artwork lives. Data lives in paintedTemplates.ts; rendering in
// components/invite/PaintedCard.tsx.

import { blend, cleanHex, isDarkColor, luminance } from './inviteStyle'
import type { TemplateCategory } from './inviteTemplates'

const contrastRatio = (a: string, b: string) => {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** How a template's art is made — this decides what a host can recolour. */
export type PaintedLook = 'watercolor' | 'botanical' | 'photo' | 'line' | 'popup'

export type PaintedMotion = 'bob' | 'hop' | 'sway' | 'drift' | 'spin' | 'breathe'

/** Offsets in percent of the card's width (left/right) or height (top/bottom). */
export type PaintedPos = { left?: number; right?: number; top?: number; bottom?: number }

export type PaintedPiece = {
  /** File stem in public/templates/painted/<id>/. */
  key: string
  pos: PaintedPos
  /** Width in percent of the card's width. */
  w: number
  /** Height ÷ width of the image, so the box can be sized before it loads. */
  r: number
  motion: PaintedMotion
}

export type PaintedColorway = {
  key: string
  label: { en: string; ka: string }
  swatch: string
  /** Stems that have a `-<key>` variant; everything else is shared. */
  files: string[]
  /** The name colour this colourway reads best in. */
  accent?: string
}

export type PaintedTypeset =
  | 'rounded'
  | 'cartoon'
  | 'bubbly-script'
  | 'glam-script'
  | 'garden-script'
  | 'festive'
  | 'deco'
  | 'rustic'
  | 'soft'
  | 'poster-bold'

export type PaintedDef = {
  id: string
  look: PaintedLook
  category: TemplateCategory
  label: { en: string; ka: string }
  blurb: { en: string; ka: string }
  tags: string[]
  /** INVITE_FONTS id the Design step shows as this template's own type. */
  font: string
  /** Paper for the rest of the invitation page (and the botanical/line card). */
  paper: string
  /** The painting's tone where the words sit (watercolour, photo). */
  zonePaper?: string
  dark?: boolean
  /** Small type on the card. */
  ink: string
  /** Colours offered for the card's type; the first is the design's own. */
  accents: string[]
  /** Papers offered (botanical, line); the first is the design's own. */
  papers?: string[]
  typeset?: PaintedTypeset
  date?: 'block' | 'line'
  /** Vertical band the words are centred in, percent of the card height. */
  zone?: [number, number]
  /** Side inset of the words, percent of the card width. */
  cx?: number
  pieces?: PaintedPiece[]
  defaultSwatch?: string
  colorways?: PaintedColorway[]
  kicker?: { en: string; ka: string }
  plant?: { left: number; bottom: number; width: number; r: number }
  copy?: { left: number; right: number; top: number }
  art?: Record<'garland' | 'left' | 'right', { pos: PaintedPos; w: number; r: number }>
  /** The photo's still life cut out as `subject.webp` — the part of a poster that moves. */
  subject?: { left: number; top: number; w: number; r: number }
  /** A pop-up card's paper layers (look `popup`). */
  popup?: PopupArt
}

/**
 * A pop-up card is a 3D fold: a floor panel lying on the table and a back
 * panel hinged to it, with die-cut layers standing on the floor and floaters
 * pinned in front of the back panel. Lengths are in card units, where the
 * card is 100 wide; `y` is a layer's distance from the fold, `top` a
 * floater's distance from the back panel's top edge.
 */
export type PopupLayer = {
  /** File stem in public/templates/popup/<id>/. */
  key: string
  iw: number
  ih: number
  x: number
  y: number
  w: number
  /** Degrees past upright the layer leans back once it stands. */
  lean: number
  /** The sign that carries the words: its blank area as [left, top, right, bottom] fractions. */
  plaque?: [number, number, number, number]
}

export type PopupFloat = 'bob' | 'glide' | 'twinkle' | 'swing' | 'flutter'

export type PopupFloater = {
  key: string
  iw: number
  ih: number
  x: number
  top: number
  w: number
  /** Distance in front of the back panel. */
  z: number
  anim: PopupFloat
}

export type PopupArt = {
  /** Back panel height for a 100-wide card. */
  bh: number
  /** The table the card lies on: centre and edge of its gradient. */
  table: [string, string]
  floor: string
  dot: string
  edge: string
  cover: string
  coverInk: string
  /** The sign's blank surface, which the words are made readable against. */
  plaque: string
  /** Light words on a dark sign get a soft shadow. */
  lightText?: boolean
  /** Title size as a share of the sign's width, and its weight. */
  titleSize: number
  titleWeight: number
  layers: PopupLayer[]
  floaters: PopupFloater[]
  /** Where the sign's words sit on the pre-rendered thumbnail, percent of the card. */
  thumbText: { left: number; top: number; width: number; height: number }
}

export const PAINTED_LOOKS: Array<{ id: PaintedLook; label: { en: string; ka: string }; blurb: { en: string; ka: string } }> = [
  {
    id: 'watercolor',
    label: { en: 'Watercolour', ka: 'აკვარელი' },
    blurb: { en: 'Painted scenes with cut-outs that move', ka: 'ნახატი სცენები მოძრავი დეტალებით' },
  },
  {
    id: 'botanical',
    label: { en: 'Botanical', ka: 'ბოტანიკური' },
    blurb: { en: 'Vintage flower plates and handwritten names', ka: 'ძველი ბოტანიკური ილუსტრაციები და ხელნაწერი' },
  },
  {
    id: 'photo',
    label: { en: 'Photo poster', ka: 'ფოტო-პოსტერი' },
    blurb: { en: 'Sunlit still lifes with a bold headline', ka: 'მზის შუქზე ნატურმორტი და დიდი სათაური' },
  },
  {
    id: 'line',
    label: { en: 'Line art', ka: 'ხაზოვანი' },
    blurb: { en: 'One-ink drawings in any colour', ka: 'ერთფეროვანი ჩანახატები ნებისმიერ ფერში' },
  },
  {
    id: 'popup',
    label: { en: 'Pop-up', ka: 'გასაშლელი' },
    blurb: { en: 'Paper cards that unfold in 3D when opened', ka: 'ქაღალდის ბარათები, რომლებიც გახსნისას 3D-ში იშლება' },
  },
]

export const paintedLookLabel = (look: PaintedLook, lang: string) => {
  const l = PAINTED_LOOKS.find((x) => x.id === look)
  return l ? (lang === 'ka' ? l.label.ka : l.label.en) : look
}

export const PAINTED_ASSETS = '/templates/painted'
export const POPUP_ASSETS = '/templates/popup'

export const popupAsset = (def: PaintedDef, stem: string) => `${POPUP_ASSETS}/${def.id}/${stem}.webp`

export const paintedAsset = (def: PaintedDef, stem: string, colorway?: string | null) => {
  const cw = colorway ? def.colorways?.find((c) => c.key === colorway) : null
  const file = cw && cw.files.includes(stem) ? `${stem}-${cw.key}` : stem
  return `${PAINTED_ASSETS}/${def.id}/${file}.webp`
}

/* ------------------------------------------------------------ colour rules */

const sameHex = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/** Does the ARTWORK take the host's colour too, or only the words on it? */
export const allowsAnyColour = (def: PaintedDef) => def.look === 'line'

export type PaintedChoice = { accent: string; paper: string; colorway: string | null }

/**
 * The look a painted card is drawn in, from the host's stored overrides.
 *
 * The words take any colour the host picks — the card lifts it against the
 * surface it lands on (`readableOnPaper`), so no pick is ever unreadable. The
 * background is the part the art decides: a botanical plate and a line drawing
 * are printed on a flat paper and take any colour too, while a watercolour or
 * a photograph IS the background and keeps its own (a stored background then
 * only picks a painted colourway, when it matches one's swatch). A background
 * left over from a flat-paper template therefore never repaints a painting.
 * It DOES carry from one flat paper to the next, as the classic templates'
 * accent and paper always have — the host picked that colour, the picker
 * shows it as theirs, and one tap on the first swatch gives the design back.
 */
export const resolvePaintedChoice = (
  def: PaintedDef,
  accent?: string | null,
  background?: string | null,
): PaintedChoice => {
  const colorway =
    def.colorways?.find((c) => sameHex(c.swatch, background))?.key || null
  const colorwayAccent = colorway ? def.colorways?.find((c) => c.key === colorway)?.accent : null
  const pickedAccent = cleanHex(accent)
  const pickedPaper = def.papers?.length ? cleanHex(background) : ''
  return {
    accent: pickedAccent || colorwayAccent || def.accents[0],
    paper: pickedPaper || def.paper,
    colorway,
  }
}

/**
 * The overrides worth passing on to the rest of the invitation page (buttons,
 * countdown, RSVP card): only what the card itself honoured.
 */
export const paintedPageOverrides = (def: PaintedDef, choice: PaintedChoice) => ({
  accent: sameHex(choice.accent, def.accents[0]) ? null : choice.accent,
  background: def.papers && !sameHex(choice.paper, def.paper) ? choice.paper : null,
})

/**
 * Walk `color` away from `surface` until it clears `min` — lighter on a dark
 * paper, darker on a light one, one seventh of the way at a time and no
 * further than it has to go, so the hue the host picked survives the trip.
 * A mid-tone paper can leave no room in the direction its darkness suggests
 * (light type on mid grey tops out around 3:1), so when the walk comes up
 * short the other end is tried and the more readable of the two wins.
 */
export const readableOnPaper = (color: string, surface: string, min: number) => {
  const walk = (toward: string) => {
    let out = color
    for (let step = 1; step <= 14 && contrastRatio(out, surface) < min; step += 1) {
      out = blend(color, toward, step * 0.07)
    }
    return out
  }
  const first = walk(isDarkColor(surface) ? '#ffffff' : '#000000')
  if (contrastRatio(first, surface) >= min) return first
  const other = walk(isDarkColor(surface) ? '#000000' : '#ffffff')
  return contrastRatio(other, surface) > contrastRatio(first, surface) ? other : first
}

/* --------------------------------------------------------------- the words */

export const CATEGORY_KICKERS: Record<TemplateCategory, { en: string; ka: string }> = {
  kids: { en: 'You’re invited to', ka: 'გეპატიჟებით' },
  birthday: { en: 'Please join us to celebrate', ka: 'ერთად აღვნიშნოთ' },
  wedding: { en: 'Please join us to celebrate', ka: 'გეპატიჟებით' },
  baby: { en: 'Please join us for', ka: 'გეპატიჟებით' },
  corporate: { en: 'You’re invited to', ka: 'გეპატიჟებით' },
  holiday: { en: 'Come celebrate with us', ka: 'ერთად ვიზეიმოთ' },
  party: { en: 'You’re invited to', ka: 'გეპატიჟებით' },
  outdoor: { en: 'Join us for', ka: 'შემოგვიერთდით' },
  elegant: { en: 'You are cordially invited to', ka: 'გეპატიჟებით' },
}

export const kickerFor = (def: PaintedDef, lang: string) => {
  const k = def.kicker || CATEGORY_KICKERS[def.category]
  return lang === 'ka' ? k.ka : k.en
}

const MONTHS = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ka: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
}
const WEEKDAYS = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ka: ['კვირა', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი'],
}

export type PaintedDate = {
  weekday: string
  month: string
  day: string
  year: string
  /** "20.08.2026" */
  dotted: string
  /** "20/08" — the poster's numeral */
  short: string
  /** "4 PM" / "16:00", empty when there is no time */
  time: string
}

/** Calendar parts of an event date (YYYY-MM-DD, read as a calendar day, no timezone shift). */
export const paintedDate = (date?: string | null, time?: string | null, lang = 'en'): PaintedDate => {
  const l = lang === 'ka' ? 'ka' : 'en'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '')
  const t = /^(\d{1,2}):(\d{2})/.exec(time || '')
  let clock = ''
  if (t) {
    const h = Number(t[1])
    const mm = t[2]
    if (l === 'ka') clock = `${String(h).padStart(2, '0')}:${mm}`
    else clock = `${h % 12 || 12}${mm === '00' ? '' : `:${mm}`} ${h < 12 ? 'AM' : 'PM'}`
  }
  if (!m) return { weekday: '', month: '', day: '', year: '', dotted: '', short: '', time: clock }
  const [, y, mo, d] = m
  const wd = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay()
  return {
    weekday: WEEKDAYS[l][wd],
    month: MONTHS[l][Number(mo) - 1],
    day: String(Number(d)),
    year: y,
    dotted: `${d}.${mo}.${y}`,
    short: `${d}/${mo}`,
    time: clock,
  }
}

/**
 * A title size that fits: the longest word must fit the column on its own, and
 * the whole title within `lines` lines. `k` is the face's average advance per
 * em, measured once per typeset. Returns a cqw value.
 */
export const fitTitle = (text: string, opts: { max: number; min: number; k: number; width: number; lines: number }) => {
  const chars = Array.from(text || ' ').length
  const longest = Math.max(1, ...String(text || '').split(/\s+/).map((w) => Array.from(w).length))
  const byWord = opts.width / (longest * opts.k)
  const byLines = (opts.width * opts.lines) / (chars * opts.k)
  return Math.max(opts.min, Math.min(opts.max, byWord, byLines))
}

/** Split a title into two balanced lines at a word boundary (poster, script). */
export const splitTwo = (text: string): [string, string] => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean)
  if (words.length < 2) return [words[0] || '', '']
  let best = 1
  let bestDiff = Infinity
  for (let i = 1; i < words.length; i += 1) {
    const a = words.slice(0, i).join(' ').length
    const b = words.slice(i).join(' ').length
    if (Math.abs(a - b) < bestDiff) {
      bestDiff = Math.abs(a - b)
      best = i
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')]
}

/** "Nino & Luka" / "ნინო და ლუკა" → two lines with the ampersand leading the second. */
export const splitNames = (text: string): [string, string] => {
  const m = /^(.+?)\s+(&|და|and)\s+(.+)$/i.exec(String(text || '').trim())
  if (m) return [m[1], `${m[2]} ${m[3]}`]
  return splitTwo(text)
}

/** "Mziuri Park, Tbilisi" → ["Mziuri Park,", "Tbilisi"]. */
export const splitPlace = (place: string): string[] => {
  const s = String(place || '').trim()
  const i = s.indexOf(',')
  if (i < 0 || s.length < 22) return s ? [s] : []
  return [s.slice(0, i + 1), s.slice(i + 1).trim()]
}
